import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import classNames from 'classnames';
import { format } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { ReactElement, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useQuery from '~/store/medicationLogistics/hooks/useQuery';
import useRole from '~/store/user/hooks/useRole';
import { IClientOptionByTeam, IGetClientRequestPayload } from '~/store/client/types';
import { extractErrorCode, extractErrorMessage } from '~/utils/error/error';
import formatClientOptions from '~/utils/formatClientOptions';
import useMemoCompare from '~/hooks/useMemoCompare';
import useQueryParam from '~/ui/pages/MedicationLogistics/hooks/useQueryParam';

import Toggle from '~/ui/components/inputs/Toggle';
import ClientSelect from '~/ui/pages/Clients/reusable/ClientSelect';
import { ReactComponent as ArrowIcon } from '~/ui/assets/images/arrowBack.svg';
import arrowUpIcon from '~/ui/assets/images/arrowUp.svg';
import Loader from '../Loader';
import Allergies from './components/Allergies';
import MainInfo from './components/MainInfo';
import MonitoredBy from './components/MonitoredBy';
import MonthControls from './components/MonthControls';
import PrintableMonitoredBy from './components/PrintableMonitoredBy';

import SortPastVisits from './components/SortPastVisits';
import { SORT_BY } from './constants';
import { IFormValues, SortClientInfoBy } from './types';

import toggleStyles from '~/ui/components/inputs/Toggle/Toggle.module.scss';

import styles from './ClientInfo.module.scss';

interface IProps {
  loading: boolean;
  clientId?: number;
  teamId: number;
  onVisitsPage?: boolean;
  printable?: boolean;
  loadMore?: () => void;
}

let prevRequestPayload: IGetClientRequestPayload;

const PAST_VISITS_LABEL = 'My Past Visits';

const ClientInfo = ({
  clientId,
  teamId,
  loading,
  onVisitsPage = false,
  printable = false,
  loadMore,
}: IProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(loading);
  const query = useQuery();
  const selectedMonthQuery = query.get('selectedMonth');
  const includeDeletedQuery = query.get('includeDeleted');
  const sortByQuery = query.get('sortBy') as SortClientInfoBy;
  const locationGroupIdQuery = query.get('locationGroupId');

  const [collapsed, setCollapsed] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(
    selectedMonthQuery ? new Date(selectedMonthQuery) : new Date(),
  );

  const { list, total, filters } = useStoreState(store => store.client.clientOptionsByTeam);
  const { current } = useStoreState(state => state.client);
  const { monitoredBy: visitsMonitoredBy } = useStoreState(state => state.visits);
  const monitoredBy = useStoreState(state => state.medicationLogistics.monitoredBy);
  const { current: user } = useStoreState(state => state.user);
  const locationGroups = useStoreState(state => state.clientAllocation.locationGroups);
  const { onGetClient, setClientOptionsByTeam, setCurrentClientOption } = useStoreActions(
    action => action.client,
  );
  const { showError } = useStoreActions(action => action.snackbar);
  const { isActTeam, isGlobalUser, isLocalAdmin, isProgramAssistant } = useRole();
  const isAdmin = isLocalAdmin || isProgramAssistant;
  const hasPastVisits = (isActTeam || isGlobalUser) && onVisitsPage;

  const availableClients = useMemoCompare(list, (prev, next) => prev && isEqual(prev, next));

  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<IFormValues>({
    defaultValues: {
      client: clientId,
      includeDeleted: isAdmin ? includeDeletedQuery === 'true' : undefined,
      sortBy: hasPastVisits ? sortByQuery || SORT_BY.CLIENT : undefined,
    },
  });

  const locationGroupId = locationGroupIdQuery ? Number(locationGroupIdQuery) : null;
  const locationGroupClients = useMemo(
    () => locationGroups.find(group => group.locationGroup.id === locationGroupId)?.clients || [],
    [locationGroups, locationGroupId],
  );

  const allClients = useMemo(
    () => (locationGroupId ? locationGroupClients : availableClients),
    [locationGroupId, locationGroupClients, availableClients],
  );

  const formattedClientOptions = useMemo(() => formatClientOptions(allClients), [allClients]);

  const { client, includeDeleted, sortBy } = watch();

  const isPastVisits = (!clientId || clientId === -1 || client === -1) && hasPastVisits;

  const clientsOptions = useMemo(
    () =>
      hasPastVisits
        ? [{ value: -1, label: PAST_VISITS_LABEL }, ...formattedClientOptions]
        : formattedClientOptions,
    [formattedClientOptions, hasPastVisits],
  );

  const isPrevSelectedExist: boolean = useMemo(
    () => !!clientsOptions.find(c => c.value === client)?.value || isPastVisits,
    [clientsOptions, isPastVisits, client],
  );

  const firstOption = useMemo(() => clientsOptions?.[0]?.value, [clientsOptions]);
  const secondOption = useMemo(() => clientsOptions?.[1]?.value, [clientsOptions]);
  // Default active client
  const thirdOption = useMemo(() => {
    const activeClients = (allClients as IClientOptionByTeam[]).filter(c =>
      c.teams?.some(t => t.id === teamId && !t.isArchived),
    );

    return formatClientOptions(activeClients)?.[0]?.value;
  }, [allClients, teamId]);

  useEffect(() => {
    if (!isPrevSelectedExist && firstOption && !filters.name) {
      setValue('client', Number(firstOption));
    }
  }, [isPrevSelectedExist, firstOption, setValue, filters.name]);

  useEffect(() => {
    if (isAdmin) {
      const urlIncludeDeleted = includeDeletedQuery === 'true';
      if (urlIncludeDeleted !== includeDeleted) {
        setValue('includeDeleted', urlIncludeDeleted);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeletedQuery, isAdmin]);

  useEffect(() => {
    if (current?.isArchived && !includeDeleted && secondOption && hasPastVisits) {
      setCurrentClientOption([]);
      setValue('client', Number(secondOption));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.isArchived, includeDeleted, setClientOptionsByTeam, secondOption, hasPastVisits]);

  const diffClients = useMemo(() => client !== current?.id, [client, current?.id]);

  // as a global user we need to get team id from selected client option to fetch extended client info
  // for all other roles we pass it from parent component
  const clientTeamId = isGlobalUser
    ? availableClients.find(c => c.id === client)?.teams.find(t => t.id === teamId)?.id ||
      availableClients.find(c => c.id === client)?.teams[0]?.id
    : teamId;

  const needFetchClient = useMemo(() => {
    if ((client > 0 || diffClients) && clientTeamId) {
      prevRequestPayload = null;
      // validate that selected client and client team id match to prevent wrong requests
      return !!availableClients.find(
        item =>
          item.id === client && (item.teams.some(t => t.id === clientTeamId) || includeDeleted),
      );
    }
    return false;
  }, [availableClients, client, diffClients, clientTeamId, includeDeleted]);

  const roleId = user?.roleId;

  const selectClient = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!needFetchClient) {
        return;
      }
      const requestPayload = {
        clinicId: String(user?.clinic.id),
        teamId: String(clientTeamId),
        clientId: String(client),
        roleId,
      };

      if (isEqual(prevRequestPayload, requestPayload)) {
        return;
      }

      await onGetClient(requestPayload);
      prevRequestPayload = requestPayload;
    } catch (e) {
      if (extractErrorCode(e) !== 'entity_not_found' && extractErrorCode(e) !== 'forbidden') {
        showError(extractErrorMessage(e));
      }
    } finally {
      setIsLoading(false);
    }
  }, [needFetchClient, user?.clinic.id, clientTeamId, client, roleId, onGetClient, showError]);

  useEffect(() => {
    selectClient();
  }, [selectClient]);

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  useQueryParam('clientId', String(client));
  useQueryParam('selectedMonth', format(selectedMonth, 'yyyy-MM-dd'));
  useQueryParam('includeDeleted', isAdmin ? String(!!includeDeleted) : undefined);
  useQueryParam('sortBy', sortBy);

  const currentClientIndex = useMemo(
    () => (isPastVisits ? 0 : clientsOptions.findIndex(option => option.value === clientId)),
    [clientsOptions, clientId, isPastVisits],
  );

  const isLastClientOption = useMemo(
    () => clientsOptions.length - 1 === currentClientIndex,
    [clientsOptions, currentClientIndex],
  );
  const isFirstClientOption = currentClientIndex === 0;

  const onClickBack = () => {
    const newClientId = currentClientIndex
      ? clientsOptions[currentClientIndex - 1]?.value
      : clientsOptions[0]?.value;

    if (newClientId) {
      setValue('client', Number(newClientId));
    }
  };

  const onClickForward = () => {
    const newClientId =
      clientsOptions.length - 1 !== currentClientIndex
        ? clientsOptions[currentClientIndex + 1]?.value
        : clientsOptions[clientsOptions.length - 1]?.value;

    if (newClientId) {
      setValue('client', Number(newClientId));
    }
  };

  const needLoadMore = useMemo(
    () => total > clientsOptions.length && clientsOptions.length - 2 === currentClientIndex,
    [clientsOptions.length, currentClientIndex, total],
  );

  const loadMoreAndForward = async () => {
    // if the user reaches the end of the list using the arrow buttons, we need to load more items before they reach the last item
    if (needLoadMore && loadMore) {
      loadMore();
    }
    onClickForward();
  };

  const onChangeIncludeDeletedCallback = () => {
    if (includeDeleted && thirdOption !== undefined) {
      setCurrentClientOption([]);
      setValue('client', Number(thirdOption));
    }
  };

  const { allergies } = current || {};

  return (
    <section
      className={classNames(styles.infoSection, {
        [styles.collapsedInfoSection]: collapsed,
        [styles.contentEnd]: isPastVisits,
      })}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <>
            {!isPastVisits ? <MainInfo collapsed={collapsed} onVisitsPage={onVisitsPage} /> : null}
            {!collapsed && (
              <>
                {current && !isPastVisits ? (
                  <>
                    <MonitoredBy monitoredBy={onVisitsPage ? visitsMonitoredBy : monitoredBy} />
                    {!onVisitsPage && <PrintableMonitoredBy monitoredBy={monitoredBy} />}{' '}
                  </>
                ) : null}
                {!!allergies && !isPastVisits && <Allergies allergies={allergies} />}
                {isPastVisits && (
                  <Box
                    className={classNames(styles.column, styles.centerItems, {
                      [styles.left]: isPastVisits,
                    })}
                  >
                    <MonthControls
                      selectedMonth={selectedMonth}
                      setSelectedMonth={setSelectedMonth}
                      noClient
                    />
                  </Box>
                )}
                {isPastVisits && (
                  <div className={styles.sortPastVisitsContainer}>
                    <SortPastVisits control={control} errors={errors} />
                  </div>
                )}

                <Box
                  className={classNames(styles.column, styles.centerItems, {
                    [styles.right]: isPastVisits,
                  })}
                >
                  <Box
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      display: 'flex',
                      width: '100%',
                    }}
                  >
                    <IconButton
                      onClick={onClickBack}
                      disabled={isFirstClientOption}
                      style={{ opacity: isFirstClientOption ? 0.3 : 1 }}
                      size="large"
                    >
                      <ArrowIcon width={10} height={10} />
                    </IconButton>
                    <ClientSelect
                      control={control}
                      errors={errors}
                      loading={loading}
                      loadMore={loadMore}
                      additionalOption={
                        hasPastVisits ? { value: -1, label: PAST_VISITS_LABEL } : undefined
                      }
                    />
                    <IconButton
                      className={styles.arrowRight}
                      onClick={needLoadMore ? loadMoreAndForward : onClickForward}
                      disabled={isLastClientOption}
                      style={{ opacity: isLastClientOption ? 0.3 : 1 }}
                      size="large"
                    >
                      <ArrowIcon width={10} height={10} />
                    </IconButton>
                  </Box>
                  {isAdmin && !printable && (
                    <Box
                      className={classNames(styles.toggle, toggleStyles.toggleContainer)}
                      sx={{
                        marginTop: 1,
                        alignItems: 'center',
                      }}
                    >
                      <Toggle
                        name="includeDeleted"
                        size="small"
                        control={control}
                        label="Display Archived Clients"
                        errors={errors}
                        labelPlacement="start"
                        cb={onChangeIncludeDeletedCallback}
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
          {!isPastVisits && (
            <Box
              className={classNames(styles.collapseButton, { [styles.collapsedIcon]: collapsed })}
              onClick={handleCollapse}
            >
              <img src={arrowUpIcon} alt="arrow-up" />
            </Box>
          )}
        </>
      )}
    </section>
  );
};

export default memo(ClientInfo);
